import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
} from "recharts";
import { Save, Edit3, Plus, Settings, Trash2, X, Check } from "lucide-react";

const EditableInput = ({ value, onChange, className }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`border rounded px-2 py-1 ${className}`}
  />
);

const SafeChart = ({ chartsReady, children }) =>
  chartsReady ? children : <div>Loading chart...</div>;

const COLUMN_COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
  "#87d068",
  "#ffa39e",
  "#b7eb8f",
  "#87e8de",
  "#bae637",
];

const IncomeTab = ({
  incomeData = [],
  incomeColumns = ["salary", "business", "rental", "crypto", "affiliate"],
  editMode,
  onToggleEdit,
  updateIncomeData,
  onUpdateColumns,
  chartsReady = true,
  addNextMonth,
  setIncomeData, // 👈 required for flexible month insertion
}) => {
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [showMonthInsertModal, setShowMonthInsertModal] = useState(false);
  const [tempColumns, setTempColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");
  
  // For month insertion
  const [newMonthName, setNewMonthName] = useState("");
  const [insertPosition, setInsertPosition] = useState("end");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  const openColumnEditor = () => {
    setTempColumns([...incomeColumns]);
    setShowColumnEditor(true);
  };

  const addColumn = () => {
    if (newColumnName.trim() && !tempColumns.includes(newColumnName.trim().toLowerCase())) {
      setTempColumns([...tempColumns, newColumnName.trim().toLowerCase()]);
      setNewColumnName("");
    }
  };

  const removeColumn = (columnToRemove) => {
    setTempColumns(tempColumns.filter((col) => col !== columnToRemove));
  };

  const saveColumns = () => {
    if (tempColumns.length > 0) onUpdateColumns(tempColumns);
    setShowColumnEditor(false);
  };

  const cancelColumnEdit = () => {
    setTempColumns([]);
    setNewColumnName("");
    setShowColumnEditor(false);
  };

  const formatColumnName = (columnName) =>
    columnName.charAt(0).toUpperCase() + columnName.slice(1);

  /* ─── Month Insertion Logic ──────────────────────────────────────────── */
  const openMonthInsertModal = () => {
    setNewMonthName("");
    setInsertPosition("end");
    setSelectedMonthIndex(0);
    setShowMonthInsertModal(true);
  };

  const handleInsertMonth = () => {
    if (!newMonthName.trim()) {
      alert("Please enter a month name");
      return;
    }

    // Create new month object with all columns initialized to 0
    const newMonth = { month: newMonthName.trim(), total: 0 };
    incomeColumns.forEach((col) => {
      newMonth[col] = 0;
    });

    const updatedData = [...incomeData];
    
    if (insertPosition === "beginning") {
      updatedData.unshift(newMonth);
    } else if (insertPosition === "end") {
      updatedData.push(newMonth);
    } else if (insertPosition === "before") {
      updatedData.splice(selectedMonthIndex, 0, newMonth);
    } else if (insertPosition === "after") {
      updatedData.splice(selectedMonthIndex + 1, 0, newMonth);
    }

    setIncomeData(updatedData);
    setShowMonthInsertModal(false);
  };

  /* ─── Delete Month Handler ───────────────────────────────────────────── */
  const deleteMonth = (monthIndex) => {
    if (window.confirm("Are you sure you want to delete this month?")) {
      const updated = incomeData.filter((_, idx) => idx !== monthIndex);
      setIncomeData(updated);
    }
  };

  return (
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold">Income Tracking</h3>

        <div className="flex gap-2">
          <button
            onClick={openMonthInsertModal}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white hover:opacity-90"
          >
            <Plus size={16} /> Add Month
          </button>

          <button
            onClick={openColumnEditor}
            className="flex items-center gap-2 px-3 py-2 rounded-lg btn-outline border-arc-line"
          >
            <Settings size={16} /> Columns
          </button>

          <button
            onClick={onToggleEdit}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              editMode ? "bg-green-600 text-white" : "bg-blue-600 text-white"
            } hover:opacity-90`}
          >
            {editMode ? <Save size={16} /> : <Edit3 size={16} />}
            {editMode ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      {/* Month Insert Modal */}
      {showMonthInsertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-arc-elevated rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Insert New Month</h4>
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="text-arc-dim hover:text-arc-muted"
              >
                <X size={20} />
              </button>
            </div>

            {/* Month Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Month Name (e.g., "Jan 2025", "February 2025")
              </label>
              <input
                type="text"
                value={newMonthName}
                onChange={(e) => setNewMonthName(e.target.value)}
                placeholder="Enter month name..."
                className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Insert Position
              </label>
              <select
                value={insertPosition}
                onChange={(e) => setInsertPosition(e.target.value)}
                className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginning">At the beginning</option>
                <option value="end">At the end</option>
                {incomeData.length > 0 && (
                  <>
                    <option value="before">Before a specific month</option>
                    <option value="after">After a specific month</option>
                  </>
                )}
              </select>
            </div>

            {/* Specific Month Selection (if before/after) */}
            {(insertPosition === "before" || insertPosition === "after") && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Select Month
                </label>
                <select
                  value={selectedMonthIndex}
                  onChange={(e) => setSelectedMonthIndex(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {incomeData.map((row, idx) => (
                    <option key={idx} value={idx}>
                      {row.month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="px-4 py-2 btn-outline border-arc-line rounded-lg hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertMonth}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Check size={16} /> Insert Month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Editor Modal */}
      {showColumnEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-arc-elevated p-6 rounded-lg max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold mb-4">Manage Income Columns</h4>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Current Columns:</label>
              <div className="space-y-2">
                {tempColumns.map((column) => (
                  <div
                    key={column}
                    className="flex items-center justify-between bg-white/[0.03] p-2 rounded"
                  >
                    <span>{formatColumnName(column)}</span>
                    <button
                      onClick={() => removeColumn(column)}
                      className="text-red-500 hover:text-red-700"
                      disabled={tempColumns.length <= 1}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Add New Column:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Column name..."
                  className="flex-1 border rounded px-3 py-2"
                  onKeyPress={(e) => e.key === "Enter" && addColumn()}
                />
                <button
                  onClick={addColumn}
                  disabled={!newColumnName.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:opacity-90 disabled:opacity-50"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={cancelColumnEdit}
                className="px-4 py-2 border border-arc-line rounded hover:bg-white/[0.03]"
              >
                Cancel
              </button>
              <button
                onClick={saveColumns}
                className="px-4 py-2 bg-green-600 text-white rounded hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editable Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto">
          <thead>
            <tr className="bg-white/[0.03]">
              <th className="px-4 py-2 text-left">Month</th>
              {incomeColumns.map((column) => (
                <th key={column} className="px-4 py-2 text-right">
                  {formatColumnName(column)}
                </th>
              ))}
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {incomeData.map((row, idx) => (
              <tr key={`${row.month}-${idx}`} className="border-b">
                <td className="px-4 py-2 font-medium">{row.month}</td>

                {incomeColumns.map((column) => (
                  <td key={column} className="px-4 py-2 text-right">
                    {editMode ? (
                      <EditableInput
                        value={row[column] || 0}
                        onChange={(val) => updateIncomeData(idx, column, val)}
                        className="w-20 text-right"
                      />
                    ) : (
                      (row[column] || 0).toLocaleString()
                    )}
                  </td>
                ))}

                <td className="px-4 py-2 text-right font-semibold text-green-600">
                  ${(row.total || 0).toLocaleString()}
                </td>

                {/* Delete Month button */}
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => deleteMonth(idx)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete Month"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Average + total summary */}
      {incomeData.length > 0 && (() => {
        const total = incomeData.reduce((s, r) => s + (r.total || 0), 0);
        const months = incomeData.length;
        const avg = months > 0 ? total / months : 0;
        const fmt = (n) =>
          `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
        return (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Avg Monthly Income</p>
              <p className="text-2xl num text-arc-gold">{fmt(avg)}</p>
              <p className="text-xs text-arc-dim mt-1">across {months} months tracked</p>
            </div>
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Total Income</p>
              <p className="text-2xl num text-arc-ink">{fmt(total)}</p>
              <p className="text-xs text-arc-dim mt-1">all months combined</p>
            </div>
            <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
              <p className="eyebrow mb-1">Latest Month</p>
              <p className="text-2xl num text-arc-ink">{fmt(incomeData[incomeData.length - 1]?.total || 0)}</p>
              <p className="text-xs text-arc-dim mt-1">{incomeData[incomeData.length - 1]?.month || "—"}</p>
            </div>
          </div>
        );
      })()}

      {/* Chart */}
      <div className="mt-6">
        <SafeChart chartsReady={chartsReady}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={incomeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Legend />
              {incomeColumns.map((column, index) => (
                <Bar
                  key={column}
                  dataKey={column}
                  stackId="a"
                  fill={COLUMN_COLORS[index % COLUMN_COLORS.length]}
                  name={formatColumnName(column)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </SafeChart>
      </div>

      {/* Info */}
      <div className="mt-4 text-sm text-arc-muted">
        <p>
          Click "Add Month" to insert a month at any position in your income tracking.
        </p>
      </div>
    </div>
  );
};

export default IncomeTab;